// Tab metadata for the Accordion's data-driven chart tabs, used to build
// the "Visible Charts" multi-select. "Options" isn't included here -- it's
// always shown, not togglable.
//
// `gate` names a deriveDesignInfo() flag: a tab with one is only turned on
// by default when that flag is true for the detected design type (e.g.
// Monte Carlo-only tabs default off for a Factorial experiment). A tab with
// no gate defaults on whenever its chart data exists. The gate only affects
// the *default* selection -- once picked, a tab stays available in the
// multi-select regardless of design type, since the user asked for it.
// These two default on and don't count against MAX_DEFAULT_CHART_TABS below --
// they were previously always shown, unconditionally.
const ALWAYS_DEFAULT_KEYS = ["summary_stats", "best_worst_trial"];

export const CHART_TAB_DEFS = [
  { key: "summary_stats", label: "Summary Statistics" },
  { key: "best_worst_trial", label: "Best/Worst Trial" },
  { key: "throughput", label: "Throughput Analysis" },
  {
    key: "percentile_analysis",
    label: "Percentile Analysis (Monte Carlo)",
    gate: "showMonteCarloSection",
  },
  {
    key: "box_plot",
    label: "Distribution (Monte Carlo)",
    gate: "showMonteCarloSection",
  },
  {
    key: "violin_plot",
    label: "Violin Plot (Monte Carlo)",
    gate: "showMonteCarloSection",
  },
  {
    key: "cdf",
    label: "Cumulative Distribution Function",
    gate: "showMonteCarloSection",
  },
  {
    key: "main_effects",
    label: "Main Effects",
    gate: "showParameterEffectsSection",
  },
  {
    key: "significance",
    label: "Statistical Significance",
    gate: "showParameterEffectsSection",
  },
  {
    key: "parameter_ranking",
    label: "Parameter Importance Ranking",
    gate: "showFactorialSection",
  },
  {
    key: "interaction",
    label: "Interaction Effects",
    gate: "showFactorialSection",
  },
  { key: "completion_rate", label: "✓ Completion Rate Analysis" },
  { key: "total_time", label: "Simulation Time Analysis" },
  { key: "cpu_time", label: "CPU Execution Time Analysis" },
  { key: "resource_utilization", label: "Resource Utilization" },
  { key: "queue_metrics", label: "Queue Metrics" },
  { key: "wait_times", label: "Wait Times Analysis" },
];

export function getAvailableChartTabs(chartData, metadata = {}) {
  return CHART_TAB_DEFS.filter((tab) =>
    tab.key === "best_worst_trial"
      ? Boolean(metadata.best_trial || metadata.worst_trial)
      : Boolean(chartData[tab.key]),
  );
}

// Caps the default selection at 4 tabs (in CHART_TAB_DEFS priority order) so
// the Accordion doesn't open with every gated-in chart expanded -- the rest
// stay one click away in the "Visible Charts" multi-select.
const MAX_DEFAULT_CHART_TABS = 4;

export function getDefaultChartTabKeys(availableTabs, designInfo) {
  const always = availableTabs.filter((tab) =>
    ALWAYS_DEFAULT_KEYS.includes(tab.key),
  );
  const rest = availableTabs
    .filter(
      (tab) =>
        !ALWAYS_DEFAULT_KEYS.includes(tab.key) &&
        (!tab.gate || designInfo[tab.gate]),
    )
    .slice(0, MAX_DEFAULT_CHART_TABS);
  return [...always, ...rest].map((tab) => tab.key);
}
